import { useCallback, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { SNIPPET_DETECTION } from '../../../../../shared/featureFlags/flagNames';
import { Category, IssueScopeKind } from '../../../../../shared/issuesV2';
import { window } from '../../../../globals';
import { FeatureFlagRequired, LabeledCheckbox } from '../../../generic';
import IssuesSection from '../../../shared/settings/SettingsPageIssueSection';
import Section from '../../../shared/settings/SettingsPageSection';
import SnippetScanningSettingsSection from '../../../shared/settings/SnippetScanningSettingsSection';

import PropagateModal from './PropagateIssuesModal';
import useIssueSettings from './useIssueSettings';
import type { IssueSettingsProps } from './types';
import type { GenericIssueSettingsFields, LicensingIssueSettings } from './utils';

/**
 * SettingsOrganizationProjectIssuesLicensing is the settings page for
 * managing the default licensing issue settings for projects.
 */
const SettingsOrganizationProjectIssuesLicensing: VoidFunctionComponent<IssueSettingsProps> = ({
  policies: policyOptions,
}) => {
  const [isPropagateModalOpen, setIsPropagateModalOpen] = useState<boolean>(false);
  const {
    settings,
    isLoading,
    onUpdateSettings,
    policies,
    filters,
    isSaveButtonDisabled,
    onSaveSettings,
  } = useIssueSettings(
    window.user?.organizationId as number,
    Category.Licensing,
    policyOptions,
  );
  const licensingSettings = settings as LicensingIssueSettings | null;

  const renderSnippetSettings = useCallback(
    ({ isScanningEnabled, canEditPolicy }) => {
      return (
        <SnippetScanningSettingsSection
          isSnippetScanningEnabled={licensingSettings?.isSnippetScanningEnabled ?? false}
          onToggleSnippetScanning={(isChecked) =>
            onUpdateSettings({ isSnippetScanningEnabled: isChecked })}
          isScanningEnabled={isScanningEnabled}
          canEditPolicy={canEditPolicy}
        />
      );
    },
    [licensingSettings, onUpdateSettings],
  );

  const renderSnippetPropagateSettings = useCallback(
    ({ additionalFields, setAdditionalFields }) => {
      return (
        <FeatureFlagRequired flagName={SNIPPET_DETECTION}>
          <LabeledCheckbox
            label={
            licensingSettings?.isSnippetScanningEnabled
              ? `Enable snippet issue scanning for all projects`
              : `Disable snippet issue scanning for all projects`
          }
            isChecked={additionalFields.includes('projectDefaultSnippetLicensingIssueScanningEnabled')}
            onChange={() =>
              setAdditionalFields((prev: GenericIssueSettingsFields[]) =>
                prev.includes('projectDefaultSnippetLicensingIssueScanningEnabled')
                  ? prev.filter((field) => field !== 'projectDefaultSnippetLicensingIssueScanningEnabled')
                  : [...prev, 'projectDefaultSnippetLicensingIssueScanningEnabled'],
              )}
            isRippleDisabled
          />
        </FeatureFlagRequired>
      );
    }, [licensingSettings]);

  return (
    <>
      <Section>
        <Section.Left heading="Licensing">
          <Section.Paragraph>Default configuration for licensing issues on new projects.</Section.Paragraph>
          <Section.Buttons>
            <Section.PropagateButton onClick={() => setIsPropagateModalOpen(true)} isDisabled={!isSaveButtonDisabled} />
            <Section.SaveButton onClick={onSaveSettings} isLoading={isLoading.save} isDisabled={isSaveButtonDisabled} />
          </Section.Buttons>
        </Section.Left>
        <Section.Right>
          <IssuesSection
            category={Category.Licensing}
            scopeType={IssueScopeKind.Global}
            isScanningEnabled={settings?.isScanningEnabled || false}
            onToggleIsScanningEnabled={(isScanningEnabled) => onUpdateSettings({ isScanningEnabled })}
            isCICheckEnabled={settings?.isCICheckEnabled || false}
            onToggleIsCICheckEnabled={(isCICheckEnabled) => onUpdateSettings({ isCICheckEnabled })}
            policyOptions={policies.policyOptions}
            selectedPolicyOption={policies.selectedPolicyOption}
            onSelectedPolicyChange={(policyId) => onUpdateSettings({ policyId })}
            filterOptions={filters.filterOptions}
            selectedFilterOption={filters.selectedFilterOption}
            onSelectedFilterChange={(filterId) => onUpdateSettings({ filterId })}
            renderSnippetSettings={renderSnippetSettings}
            // Users with organization settings permissions can edit the issue policy and filters
            canEditFilters
            canEditPolicy
          />
        </Section.Right>
      </Section>
      {settings && (
        <PropagateModal
          category={Category.Licensing}
          isOpen={isPropagateModalOpen}
          onHide={() => setIsPropagateModalOpen(false)}
          policyName={policies.selectedPolicyName}
          filterName={filters.selectedFilterName}
          settings={settings}
          renderSnippetSettings={renderSnippetPropagateSettings}
        />
      )}
    </>
  );
};

export default SettingsOrganizationProjectIssuesLicensing;
